import React, { useMemo, useState } from 'react';

import AMSTable from '../../../helpers/ui/AMSTable/AMSTable';
import DateFnsUtils from '@date-io/date-fns';
import { MuiPickersUtilsProvider } from '@material-ui/pickers';
import { amsV3Service, WarehouseListResponse, ItemDeliveryBatchResponse } from '../../../services';
import bgLocale from 'date-fns/locale/bg';
import { findCountry } from '../../../helpers/country-helper';
import { getExtendedWarehouseId } from '../../../helpers/utils';
import { useWarehouses } from '../../../helpers/hooks';

interface StockContainerProps {
  id: number;
}

const StockContainer = (props: StockContainerProps) => {
  const { id } = props;

  const { warehouses } = useWarehouses();
  const [loading, setLoading] = useState(true);
  const [itemBatches, setItemBatches] = useState<ItemDeliveryBatchResponse[]>([]);

  React.useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      if (id) {
        const resp = await amsV3Service.getItemBatches(id, undefined, true);
        setItemBatches(resp.data.data);
      }
      setLoading(false);
    };
    loadData();
  }, [id]);

  const stockRecords = useMemo(() => {
    if (!itemBatches || !warehouses) {
      return;
    }
    const warehouseNameLookup = warehouses.reduce((acc: any, w: WarehouseListResponse) => {
      acc[w.id] = w.name;
      return acc;
    }, {});
    return itemBatches.map((s: any) => ({
      ...s,
      expirationDate: new Date(s.expirationDate),
      warehouseName: warehouseNameLookup[s.warehouseId],
    })) ?? [];
  }, [itemBatches, warehouses]);

  const columns = useMemo(
    () => [
      {
        title: 'Склад',
        field: 'warehouseId',
        render: (row: any) => {
          return `${getExtendedWarehouseId(row.warehouseId)} ${row.warehouseName}`;
        },
        cellStyle: { width: '20%' }
      },
      {
        title: 'Държава',
        field: 'country',
        render: (row: any) => {
          return `${findCountry(row.countryOfOrigin)?.name}`;
        },
        cellStyle: { width: '20%' }
      },
      {
        title: 'Срок на годност',
        field: 'expirationDate',
        type: 'date',
        cellStyle: { width: '15%' }
      },
      {
        title: 'Партида',
        field: 'batchNumber',
        cellStyle: { width: '15%' }
      },
      {
        title: 'Брой',
        field: 'quantity',
        cellStyle: { width: '15%' },
        render: (row: any) => {
          return `${row.availableQuantity} от ${row.quantity}`;
        }
      },
      {
        title: 'Цена',
        field: 'priceWithoutVat',
        cellStyle: { width: '15%' },
        align: 'right',
        render: (rowData: any) =>
          `${rowData.priceWithoutVat ?? '0.00'}${rowData.currency === 'EUR' ? ' €' : ' лв.'}`
      }
    ],
    []
  );

  return (
    <>
      <MuiPickersUtilsProvider utils={DateFnsUtils} locale={bgLocale}>
        <AMSTable
          title={'Наличности'}
          columns={columns}
          data={stockRecords}
          inlineActions={true}
          pageSize={8}
          isLoading={loading}
        />
      </MuiPickersUtilsProvider>
    </>
  );
};

export default StockContainer;
